import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { nanoid } from 'nanoid'
import { REVIEW_NAME_LIMIT } from 'shared/constants/limits'
import { logger } from 'shared/lib'
import { showToast } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { BlockerStore } from 'shared/ui/Blocker'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { getLuminanceHEXColor } from 'shared/lib/getLuminanceHEXColor'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ReviewsApi } from 'entities/Review/api/review'
import { type IResponseReview, type IReviewParams } from 'entities/Review/api/types'
import { ReviewAccount } from 'entities/Review/model/ReviewAccount'
import { NameInputStore } from 'features/CreateOrEdit/NameInput'
import { EnumModeReview, type ITabType } from 'pages/reviews/store/type'
import { ImageUploaderStore } from 'pages/reviews/ui/ImageUploader'
import { ReviewAccountsStore } from 'pages/reviews/ui/ReviewAccounts'
import { ReviewsPath } from 'pages/reviews'
import { ReviewLandingUrlModal } from 'pages/reviews/ui/ReviewLandingUrlModal/ReviewLandingUrlModal'

const defaultBackground = '#068FF9'
const defaultSubtitle = 'How would you rate your experience?'
const defaultHappySubtitle = 'Where would you like to leave a review?'
const defaultUnhappySubtitle = 'What could we do better?'

export class ReviewViewStore {
  private _id: number | null = null
  private _title = ''
  private _subtitle = defaultSubtitle
  private _happySubtitle = defaultHappySubtitle
  private _unhappySubtitle = defaultUnhappySubtitle
  private _bgColor = defaultBackground

  readonly returnTo: string
  private _activeTab: ITabType = 'appearance'
  private _scrollToStep: ITabType | null = null
  private _isLoading = true
  private _isSaving = false
  private _errorsMap = new Map<string, string>()
  private _mode: EnumModeReview = EnumModeReview.create
  private readonly _blockerStore: BlockerStore
  private readonly _nameInputStore
  private readonly _imageStore
  private readonly _accountsStore

  constructor(
    private _entities: IEntitiesStore,
    reviewResponse?: IResponseReview,
    isDuplicate?: boolean,
    returnTo?: string
  ) {
    this.returnTo = returnTo ?? `/${ReviewsPath.root}`
    this._imageStore = new ImageUploaderStore(reviewResponse?.image_url)
    const accountsData = isDuplicate
      ? reviewResponse?.accounts?.map(({ id, ...rest }) => rest)
      : reviewResponse?.accounts
    this._accountsStore = new ReviewAccountsStore(accountsData)
    this._nameInputStore = new NameInputStore({
      focusOnMount: isDuplicate,
      limit: REVIEW_NAME_LIMIT,
      isResetErrorOnChange: true,
    })
    this._blockerStore = new BlockerStore(this, {
      modalType: ModalTypeList.WARNING,
      title: 'Discard changes?',
      desc: 'You have unsaved changes',
      primaryText: 'Yes, discard',
      primaryAction: async () => {},
      hideAdditionalSecondaryAction: true,
      secondaryText: 'Go back',
    })
    makeAutoObservable(this)
    this.init(reviewResponse, isDuplicate)
  }

  init = (reviewResponse?: IResponseReview, isDuplicate?: boolean) => {
    if (reviewResponse) {
      if (!isDuplicate) {
        this._id = reviewResponse.id
        this._mode = EnumModeReview.edit
      }
      this._title = reviewResponse.title
      this._subtitle = reviewResponse.subtitle
      this._happySubtitle = reviewResponse.happy_subtitle
      this._unhappySubtitle = reviewResponse.unhappy_subtitle
      this._bgColor = reviewResponse.color
      this._nameInputStore.init(
        isDuplicate ? `Copy of ${reviewResponse.name}` : reviewResponse.name
      )
    } else {
      this._title = this._entities.organizationStore.name ?? ''
    }

    this._blockerStore.updateCache()
    this._isLoading = false
  }

  get activeTab() {
    return this._activeTab
  }

  get scrollToStep() {
    return this._scrollToStep
  }

  get isLoading() {
    return this._isLoading
  }

  get nameInputStore() {
    return this._nameInputStore
  }

  get name() {
    return this._nameInputStore.name
  }

  get imageStore() {
    return this._imageStore
  }

  get accountsStore() {
    return this._accountsStore
  }

  get accountsParams() {
    const params = this._accountsStore.accountsParams

    if (!this.isEditMode) return params

    return params.map((account) => ({ ...account, id: account.id ?? 0 }))
  }

  get accounts() {
    return this._accountsStore.accounts
  }

  get isSaving() {
    return this._isSaving
  }

  get errorsMap() {
    return this._errorsMap
  }

  get params(): IReviewParams {
    return {
      name: this.name,
      image_url: this.imageUrl,
      color: this.bgColor,
      title: this.title,
      subtitle: this.subtitle,
      happy_subtitle: this.happySubtitle,
      unhappy_subtitle: this.unhappySubtitle,
      accounts: this.accountsParams,
    }
  }

  get isEditMode() {
    return this._mode === EnumModeReview.edit
  }

  get isCreateMode() {
    return this._mode === EnumModeReview.create
  }

  get imageUrl() {
    return this._imageStore.imageUrl
  }

  get title() {
    return this._title
  }

  get subtitle() {
    return this._subtitle
  }

  get happySubtitle() {
    return this._happySubtitle
  }

  get unhappySubtitle() {
    return this._unhappySubtitle
  }

  get bgColor() {
    return this._bgColor
  }

  get isFontColorWhite() {
    const luminance = getLuminanceHEXColor(this._bgColor)
    const whiteContrast = 1.05 / (luminance + 0.05)

    return whiteContrast >= 3
  }

  get isUpdateDisabled() {
    return this.isEditMode && !this._blockerStore.isExistChanges
  }

  get blockerStore() {
    return this._blockerStore
  }

  setActiveTab = (activeTab: ITabType) => {
    this._activeTab = activeTab
  }

  setScrollToStep = (value: ITabType | null) => {
    this._scrollToStep = value
  }

  setTitle = (value: string) => {
    this._title = value
    this.setError('title', '')
  }

  setSubtitle = (value: string) => {
    this._subtitle = value
    this.setError('subtitle', '')
  }

  setHappySubtitle = (value: string) => {
    this._happySubtitle = value
    this.setError('happy_subtitle', '')
  }

  setUnhappySubtitle = (value: string) => {
    this._unhappySubtitle = value
    this.setError('unhappy_subtitle', '')
  }

  setBgColor = (value: string) => {
    this._bgColor = value
  }

  setError = (key: string, error: string) => {
    this._errorsMap.set(key, error)
  }

  removeError = (key: string) => {
    this._errorsMap.delete(key)
  }

  handleSaveReview = async () => {
    this.errorsMap.clear()
    this.checkValidate()

    if (this.errorsMap.size) {
      showToast({
        type: 'error',
        title: this.isEditMode ? 'Failed to save' : 'Failed to create',
        desc: 'Complete all required fields',
      })

      throw new Error()
    }

    try {
      runInAction(() => {
        this._isSaving = true
      })

      if (this.isEditMode && this._id) {
        await ReviewsApi.updateReview(this._id, this.params)
      } else {
        const {
          data: { data },
        } = await ReviewsApi.createReview(this.params)

        this.openReviewLandingUrlModal(data.short_url)
      }

      this._blockerStore.updateCache()

      showToast({
        type: 'success',
        title: 'Review saved',
      })

      if (!this.isEditMode) {
        uiStore.changeRoute({
          path: this.returnTo,
        })
      }
    } catch (e) {
      this.handleApiError(e)
      throw e
    } finally {
      runInAction(() => {
        this._isSaving = false
      })
    }
  }

  openReviewLandingUrlModal(shortUrl: string) {
    const modalId = nanoid()

    modalStore.addModal({
      id: modalId,
      type: ModalTypeList.DEFAULT,
      disabledOnAllClose: true,
      showCloseButton: false,
      width: 480,
      paddingContent: 0,
      ModalContent: ReviewLandingUrlModal,
      ModalContentProps: { shortUrl: shortUrl, modalId },
    })
  }

  checkValidate = () => {
    const { name, title, subtitle, accounts, happy_subtitle, unhappy_subtitle } = this.params

    if (!name.trim().length) {
      this.setError('name', 'Enter review page name')
    }

    if (!title.trim().length) {
      this.setError('title', 'Enter title')
    }

    if (!subtitle.trim().length) {
      this.setError('subtitle', 'Enter subtitle')
    }

    if (!accounts.length) {
      this.setError('accounts', 'Add review account')
    }

    if (!happy_subtitle.trim().length) {
      this.setError('happy_subtitle', 'Enter subtitle')
    }

    if (!unhappy_subtitle.trim().length) {
      this.setError('unhappy_subtitle', 'Enter subtitle')
    }
  }

  private handleApiError = (e: unknown) => {
    logger.error(e)

    if (e instanceof AxiosError && e.response) {
      const isReviewField = (key: string): key is keyof IReviewParams => key in this.params

      e.errors.entries().forEach(([key, value]) => {
        if (isReviewField(key)) {
          this.setError(key, value.value)
        } else if (key.startsWith('accounts.')) {
          const index = +key.split('.')[1]
          const account = this.accounts[index]

          if (account instanceof ReviewAccount) account.setUrlLinkError(value.value)
        }
      })
    } else {
      showToast({
        type: 'error',
        title: 'Something went wrong. Please try again',
      })
    }
  }
}
